/**
 * ChatSidebarRight — painel de detalhes da conversa.
 *
 * Arquitetura "Camadas" (desenho aprovado em 01/09/2026: proposta 18 com o topo
 * "Painel de números"): um TOPO que nunca sai da tela, um ÍNDICE de assuntos, e
 * uma PILHA onde a sub-página escolhida desliza por cima do índice sem destruí-lo
 * — é isso que preserva a rolagem do índice ao voltar.
 *
 * Este arquivo é só a casca: estado da camada aberta, moldura e a ligação com os
 * diálogos que já existiam. Cada peça mora em `details/`.
 *
 * Mockup: DOCS/superpowers/prototypes/2026-09-01-chat-details-panel/18b-topo-painel-de-numeros.html
 * Plano:  DOCS/superpowers/plans/2026-09-01-painel-detalhes-camadas.md
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { FollowUpEventResponse } from '@troia-v3/types';

import { CreateFollowUpModal } from '@/modules/calendar/components';
import { usePermissions } from '@/hooks/usePermissions';
import { Icon } from '@/shared/icons';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui';

import TagsDialog from './TagsDialog';
import DetailsHeader from './details/DetailsHeader';
import DetailsIndex from './details/DetailsIndex';
import DetailsLayer from './details/DetailsLayer';
import CompanyLayer from './details/layers/CompanyLayer';
import ContactLayer from './details/layers/ContactLayer';
import DealLayer from './details/layers/DealLayer';
import FilesLayer from './details/layers/FilesLayer';
import FollowUpsLayer from './details/layers/FollowUpsLayer';
import MembersLayer from './details/layers/MembersLayer';
import { useDetailsData, type DetailsLayerId } from './details/useDetailsData';

interface ChatSidebarRightProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
  /** #112 — em desktop estreito (região ≤1080px), o painel flutua sobre a thread */
  overlayMode?: boolean;
}

/**
 * Superfície do painel — a parte que consertou o "fundo transparente" (2026-09-01).
 *
 * O tema escuro usa superfícies TRANSLÚCIDAS de propósito: o fundo da página é uma
 * imagem e `background.sidebar` é `rgba(0,0,0,.1)`, então enquanto o painel é COLUNA
 * o que aparece atrás dele é o papel de parede — e fica certo. Quando ele vira
 * `absolute` sobre a thread, o que aparece atrás passa a ser A CONVERSA.
 *
 * `--theme-overlay-sidebar-background` existe exatamente para isso: compõe a mesma
 * tinta da sidebar sobre a cor SÓLIDA da página (ver `overlayBg` em
 * `shared/theme/cssVariableMapper.ts`), então sai opaco em qualquer tema. O celular
 * já usava; o ramo `lg:` do computador flutuante tinha ficado de fora.
 *
 * O tema claro fixo e a elevação do modo flutuante moram no CSS
 * (`.chat-details-panel--floating` em `index.css`) porque precisam redeclarar `color`
 * — cor herdada é valor JÁ RESOLVIDO, e trocar só as variáveis deixaria o texto com
 * o branco do tema escuro sobre o fundo claro.
 */
const SURFACE_MOBILE = 'max-lg:[background:var(--theme-overlay-sidebar-background)]';
const SURFACE_DESKTOP_DOCKED = 'lg:[background-color:var(--theme-sidebar-background)]';
const SURFACE_DESKTOP_FLOATING = 'lg:[background:var(--theme-overlay-sidebar-background)]';
const OVERLAY_POSITION = 'lg:absolute lg:left-auto lg:right-0 lg:top-0 lg:bottom-0 lg:z-[60]';

/** Base do contêiner, comum aos três estados (carregando, sem conversa, painel). */
const BASE_CONTAINER =
  'chat-details-panel fixed inset-x-0 bottom-0 z-[70] rounded-t-[20px] lg:rounded-none lg:w-80 lg:border-l';

const TITULO_DA_CAMADA: Record<Exclude<DetailsLayerId, 'root'>, string> = {
  contato: 'Contato',
  empresa: 'Empresa',
  negocio: 'Negócio',
  membros: 'Membros',
  arquivos: 'Arquivos',
  retornos: 'Retornos',
};

const ChatSidebarRight: React.FC<ChatSidebarRightProps> = ({
  conversationId,
  isOpen,
  onClose,
  overlayMode = false,
}) => {
  const navigate = useNavigate();

  const [layer, setLayer] = useState<DetailsLayerId>('root');
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const [isCreateFollowUpModalOpen, setIsCreateFollowUpModalOpen] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUpEventResponse | null>(null);

  // C5 — segundo dos três gates de `contacts:update` (header, painel, diálogo).
  const { hasModulePermission } = usePermissions();
  const canUpdateContacts = hasModulePermission('contacts', 'update');
  const canReadCrm = hasModulePermission('crm', 'read');

  const data = useDetailsData(conversationId, layer);
  const { conversation, isLoading, isGroup, contactId, lead } = data;

  // Trocar de conversa volta para o índice. Sem isto, abrir outra conversa
  // mantém a camada anterior aberta mostrando o assunto de outra pessoa.
  useEffect(() => {
    setLayer('root');
  }, [conversationId]);

  const voltar = useCallback(() => setLayer('root'), []);

  const funnelId = lead?.funnelId;
  const abrirNoCrm = useCallback(
    (leadId: string) => {
      // `crm/:funnelId` é a rota do quadro, e ela abre o lead por `?leadId=`
      // (ver `CrmDashboardPage`). Sem o funil não há quadro para abrir, então
      // cai na lista — que ao menos leva ao CRM em vez de não fazer nada.
      navigate(funnelId ? `/crm/${funnelId}?leadId=${leadId}` : '/crm/leads');
    },
    [navigate, funnelId],
  );

  const acoes = useMemo<CardAction[]>(() => {
    const itens: CardAction[] = [];
    if (!isGroup && contactId) {
      itens.push({ label: 'Dado do contato', icon: 'user', onClick: () => setLayer('contato') });
    }
    if (!isGroup && canReadCrm) {
      itens.push({
        label: 'Lead no CRM',
        icon: 'trending-up',
        onClick: () => {
          if (lead) abrirNoCrm(lead.id);
        },
        disabled: !lead,
        disabledReason: 'Este contato não está em nenhum funil.',
      });
    }
    itens.push({
      label: 'Novo retorno',
      icon: 'calendar',
      onClick: () => setIsCreateFollowUpModalOpen(true),
      disabled: !contactId,
      disabledReason: 'Conversa de grupo não tem retorno por contato.',
    });
    return itens;
  }, [isGroup, contactId, canReadCrm, lead, abrirNoCrm]);

  // Flutuante = superfície SOBREPOSTA: fundo opaco + tema claro fixo + elevação.
  const surfaceDesktop = overlayMode ? SURFACE_DESKTOP_FLOATING : SURFACE_DESKTOP_DOCKED;
  const floatingClass = overlayMode ? 'chat-details-panel--floating' : '';
  const position = overlayMode ? OVERLAY_POSITION : 'lg:static lg:z-auto';
  const superficie = `${SURFACE_MOBILE} ${surfaceDesktop} ${floatingClass} ${position}`;

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div
        className={`${BASE_CONTAINER} p-6 flex items-center justify-center ${superficie}`}
        style={{ borderColor: 'var(--theme-sidebar-border)' }}
      >
        <div className="text-center">
          <div
            className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-2"
            style={{ borderColor: 'var(--theme-sidebar-active)' }}
          />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Carregando...
          </p>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div
        className={`${BASE_CONTAINER} p-6 ${superficie}`}
        style={{ borderColor: 'var(--theme-sidebar-border)' }}
      >
        <p className="text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Conversa não encontrada
        </p>
      </div>
    );
  }

  const displayName =
    conversation.group?.name ||
    conversation.contact?.name ||
    conversation.subject ||
    `Conversa #${conversation.id.slice(0, 8)}`;

  return (
    <div
      data-testid="chat-sidebar-right"
      data-overlay={overlayMode ? 'true' : 'false'}
      className={`${BASE_CONTAINER} max-h-[82%] flex flex-col overflow-hidden lg:max-h-none lg:h-full ${superficie}`}
      style={{ borderColor: 'var(--theme-sidebar-border)' }}
    >
      {/* Grab da folha de baixo — só no celular */}
      <div
        className="lg:hidden flex-shrink-0 w-10 h-1 rounded-full mx-auto mt-2.5 mb-1"
        style={{ backgroundColor: 'var(--theme-input-border)' }}
      />
      {/* Cabeçalho da folha no celular (título + Fechar) */}
      <div
        className="lg:hidden flex items-center justify-between px-[18px] pt-1.5 pb-2.5 border-b flex-shrink-0"
        style={{ borderColor: 'var(--theme-sidebar-border)' }}
      >
        <span className="text-base font-bold" style={{ color: 'var(--theme-card-text)' }}>
          Detalhes
        </span>
        <button
          data-testid="chat-sidebar-close-mobile"
          onClick={onClose}
          className="text-sm font-semibold"
          style={{ color: 'var(--theme-primary)' }}
        >
          Fechar
        </button>
      </div>

      <DetailsHeader
        data={data}
        canUpdateContacts={canUpdateContacts}
        onOpenTags={() => setIsTagsDialogOpen(true)}
        actions={
          <div className="flex items-center gap-1 flex-none">
            <CardActionsMenu actions={acoes} size="sm" data-testid="details-header-actions" />
            <button
              type="button"
              data-testid="chat-sidebar-close"
              onClick={onClose}
              className="hidden lg:inline-flex cdp-iconbtn"
              title="Fechar painel"
              aria-label="Fechar painel"
            >
              <Icon name="x" className="w-4 h-4" />
            </button>
          </div>
        }
      />

      {/* Pilha: o índice fica montado embaixo; a camada desliza por cima. */}
      <div className="cdp-stack">
        <div className="cdp-pane">
          <DetailsIndex data={data} onOpen={setLayer} />
        </div>

        {layer !== 'root' && (
          <DetailsLayer title={TITULO_DA_CAMADA[layer]} testId={layer} onBack={voltar}>
            {layer === 'contato' && <ContactLayer data={data} onEdit={voltar} />}
            {layer === 'empresa' && <CompanyLayer data={data} />}
            {layer === 'negocio' && <DealLayer data={data} onOpenCrm={abrirNoCrm} />}
            {layer === 'membros' && <MembersLayer data={data} />}
            {layer === 'arquivos' && <FilesLayer data={data} conversationId={conversationId} />}
            {layer === 'retornos' && (
              <FollowUpsLayer
                data={data}
                onNew={() => setIsCreateFollowUpModalOpen(true)}
                onOpen={(followUp) => {
                  if (!contactId) return;
                  // O serviço tipa a rota `/calendar/follow-ups/contact/:id`
                  // como evento de agenda genérico, mas ela só devolve retorno
                  // — e o modal exige o tipo estreito. Estreitamos aqui de
                  // forma explícita, com o `contactId` vindo do próprio painel
                  // (é por ele que a lista foi buscada). Sem isso o caminho
                  // antigo só compilava por causa de um `any`.
                  setSelectedFollowUp({
                    ...followUp,
                    eventType: 'follow_up',
                    contactId,
                  });
                }}
              />
            )}
          </DetailsLayer>
        )}
      </div>

      {contactId && (
        <TagsDialog
          contactId={contactId}
          contactName={displayName}
          isOpen={isTagsDialogOpen}
          onClose={() => setIsTagsDialogOpen(false)}
        />
      )}

      {(isCreateFollowUpModalOpen || selectedFollowUp) && contactId && (
        <CreateFollowUpModal
          contactId={contactId}
          contactName={displayName}
          followUp={selectedFollowUp}
          onClose={() => {
            setIsCreateFollowUpModalOpen(false);
            setSelectedFollowUp(null);
            void data.refetchFollowUps();
          }}
        />
      )}
    </div>
  );
};

export default ChatSidebarRight;
